export const ENVIRONMENTS_KEYS = [
  'firebase_api_key',
  'firebase_app_id',
  'project',
  'messaging_id',
  'messaging_pub_key',
  'measurement',
  'stripe',
  'bucket_images',
  'bucket_assets',
  'bucket_gallery',
  'bucket_import',
  'bucket_orders',
  'bucket_delete',
  'bucket_tasks',
  'bucket_external_codes',
  'bucket_profile_documents',
  'rest',
  'graphql',
  'cloud_functions',
  'paypal',
  'snowit_api_key',
] as const;

export const ENVIRONMENTS_MAIN = ['development', 'production'] as const;

export const ENVIRONMENTS = [...ENVIRONMENTS_MAIN, 'emulator', 'local', 'rc', 'next'] as const;

export const COMMANDS = ['start', 'build'] as const;

export const APPS = [
  'web',
  'suspense',
  'ews',
  'ews-bikeit',
  'ews-tribala',
  // 'maintenance',
] as const;

export const SERVICES = ['ssr', 'localazier'] as const;

export const WEBAPPS = ['snowit', 'bikeit', 'tribala'] as const;

export const DEVTOOLS = ['source-map', 'hidden-source-map', 'eval-source-map'] as const;

export type EnvKeyEnum = (typeof ENVIRONMENTS_KEYS)[number];
export type EnvMainEnum = (typeof ENVIRONMENTS_MAIN)[number];
export type EnvEnum = (typeof ENVIRONMENTS)[number];
export type EnvCommandEnum = (typeof COMMANDS)[number];
export type EnvAppEnum = (typeof APPS)[number];
export type EnvServicesEnum = (typeof SERVICES)[number];
export type EnvWebAppEnum = (typeof WEBAPPS)[number];
export type Devtool = (typeof DEVTOOLS)[number];

export interface SnowitProcessEnv {
  /** api URL */
  SNOWIT_REST: string;
  /** cloudFunction URL */
  SNOWIT_CLOUDFUNCTIONS: string;
  /** app that started */
  SNOWIT_APP: EnvAppEnum;
  /** if app is web, which webapp started */
  SNOWIT_WEBAPP?: EnvWebAppEnum;
  /** firebase auth URL */
  SNOWIT_FIREBASE_AUTH_DOMAIN: string;
  /** name of bucket for assets */
  SNOWIT_BUCKET_ASSETS: string;
  /** name of bucket for images */
  SNOWIT_BUCKET_IMAGES: string;
  /** name of bucket for galleries */
  SNOWIT_BUCKET_GALLERY: string;
  SNOWIT_BUCKET_DELETE: string;
  SNOWIT_BUCKET_IMPORT: string;
  SNOWIT_BUCKET_ORDERS: string;
  SNOWIT_BUCKET_TASKS: string;
  /** name of bucket for external codes */
  SNOWIT_BUCKET_EXTERNAL_CODES: string;
  /** name of bucket for profile documents */
  SNOWIT_BUCKET_PROFILE_DOCUMENTS: string;
  /** combined firestore url for galleries */
  SNOWIT_GALLERY_URL: string;
  /** combined firestore url for images */
  SNOWIT_IMAGES_URL: string;
  /** combined firestore url for main bucket */
  SNOWIT_BUCKET_URL: string;
  /** main bucket */
  SNOWIT_FIREBASE_BUCKET: string;
  /** which command was started */
  SNOWIT_COMMAND: EnvCommandEnum;
  /** firebase database URL */
  SNOWIT_FIREBASE_DATABASE_URL: string;
  /** env that was started */
  SNOWIT_ENV: EnvEnum;
  /** if env from SNOWIT_ENV was production, otherwise development */
  SNOWIT_ENVIRONMENT: EnvMainEnum;
  /** firebase api key */
  SNOWIT_FIREBASE_API_KEY: string;
  /** GraphQL API URL */
  SNOWIT_GRAPHQL: string;
  /** SNOWIT_VERSION + git hash */
  SNOWIT_VERSION_HASHED: string;
  /** firebase app id */
  SNOWIT_FIREBASE_APP_ID: string;
  /** shorthand SNOWIT_COMMAND === build && SNOWIT_ENV === production*/
  SNOWIT_IS_PRODUCTION: boolean;
  /** mapbox id */
  SNOWIT_MAPBOX: string;
  /** firebase measurement id */
  SNOWIT_FIREBASE_MEASUREMENT_ID: string;
  SNOWIT_FIREBASE_MESSAGING_ID: string;
  SNOWIT_FIREBASE_PROJECT_ID: string;
  SNOWIT_STRIPE: string;
  /** version from package.json */
  SNOWIT_VERSION: string;
  /** date of last deploy */
  SNOWIT_DEPLOY_DATE: string;
  GITHUB_ACCESS_TOKEN?: string;
  SNOWIT_PAYPAL: string;
  /** mandatory apikey for backend calls */
  SNOWIT_API_KEY: string;
}

const DEPLOY_DEV = {
  PROJECT: 'snowit-staging',
  HOST: 'snowit-staging',
  BUCKET: 'snowit-assets-dev',
  ENVIRONMENT: 'development',
  SSRIMAGE: 'ssr-dev',
  CREDENTIALS: 'dev',
  SERVICEACCOUNT: 'firebase-adminsdk-2vhj4@snowit-staging.iam.gserviceaccount.com',
};

const DEPLOY_DEV_PANNELLO = {
  PROJECT: 'snowit-staging',
  HOST: 'snowit-staging-admin',
  BUCKET: 'snowit-assets-dev',
  ENVIRONMENT: 'development',
  CREDENTIALS: 'dev',
  SERVICEACCOUNT: 'firebase-adminsdk-2vhj4@snowit-staging.iam.gserviceaccount.com',
};

export const CONFIGS_DEPLOY = {
  development: DEPLOY_DEV,
  next: DEPLOY_DEV,
  rc: {
    PROJECT: 'snowit-02',
    HOST: 'rc-snowit',
    BUCKET: 'snowit-assets',
    ENVIRONMENT: 'production',
    SSRIMAGE: 'ssr-rc',
    CREDENTIALS: 'prod',
    SERVICEACCOUNT: 'firebase-adminsdk-vqf26@snowit-02.iam.gserviceaccount.com',
  },
  production: {
    PROJECT: 'snowit-02',
    HOST: 'snowit-newsite',
    BUCKET: 'snowit-assets',
    ENVIRONMENT: 'production',
    SSRIMAGE: 'ssr-prod',
    CREDENTIALS: 'prod',
    SERVICEACCOUNT: 'firebase-adminsdk-vqf26@snowit-02.iam.gserviceaccount.com',
  },
} as const;

export const CONFIGS_DEPLOY_PANNELLO = {
  development: DEPLOY_DEV_PANNELLO,
  next: DEPLOY_DEV_PANNELLO,
  production: {
    PROJECT: 'snowit-02',
    HOST: 'snowit-prod-admin',
    BUCKET: 'snowit-assets',
    ENVIRONMENT: 'production',
    CREDENTIALS: 'prod',
    SERVICEACCOUNT: 'firebase-adminsdk-vqf26@snowit-02.iam.gserviceaccount.com',
  },
};

export const BUN_APPS_BUILDER = [
  //
  'website',
  'widget',
  'ssr',
  'localazier',
  // 'favicons',
] as const;

export const WEB_ENVS_DEPLOY: EnvEnum[] = ['development', 'rc', 'production', 'next'];

export type Answers = {
  what: (typeof BUN_APPS_BUILDER)[number][];
  deploy: boolean;
  slack: boolean;
  environment: keyof typeof CONFIGS_DEPLOY;
};

export type AnswersWeb = {
  deploy: boolean;
  slack: boolean;
  environment: keyof typeof CONFIGS_DEPLOY;
  // maintenance: boolean;
};
export type AnswersPannello = {
  deploy: boolean;
  slack: boolean;
  environments: (keyof typeof CONFIGS_DEPLOY_PANNELLO)[];
};
